import {
  getBatchName,
  getColorCodeByLectureStatus,
  getTruncateLectureWithWidth,
} from "Pages/MyScheduleBeta/utils/unmarkedLectureHelper";
import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Popover } from "antd";
import useResponsive from "Context/useResponsive";
import LectureDetailsPopoverContent from "./LectureDetailsPopoverContent";
import "./index.css";
import DraggableModal from "utilFunctions/shared/DraggableModal";
import { inBetweenTime } from "utilFunctions/shared/ScheduleStatus";

export interface LectureCardProps {
  event: any;
  gridStartHour?: number;
  hourHeight?: number;
  onClick?: (event: any) => void;
  left?: string | number;
  right?: string | number;
  width?: string | number;
  isWeek: boolean;
  type?: string;
}

type PopoverPlacement =
  | "rightTop"
  | "rightBottom"
  | "leftTop"
  | "leftBottom"
  | "right"
  | "topRight"
  | "topLeft"
  | "bottomRight"
  | "bottomLeft"
  | "top"
  | "bottom"
  | "left";

export const getSubjectColors = (subject: string, eventType: string) => {
  if (eventType === "DOUBT") {
    return {
      border: "#f57c00",
      bg: "#fff3e0",
      text: "#e65100",
    };
  }
  const subj = subject?.toLowerCase() || "";
  if (subj.includes("physic")) {
    return {
      border: "#1a73e8",
      bg: "#e8f0fe",
      text: "#1565c0",
    };
  }
  if (subj.includes("chemist")) {
    return {
      border: "#e91e63",
      bg: "#fce4ec",
      text: "#c2185b",
    };
  }
  if (subj.includes("math") || subj.includes("bio")) {
    return {
      border: "#4caf50",
      bg: "#e8f5e9",
      text: "#2e7d32",
    };
  }
  return {
    border: "#5f6368",
    bg: "#f1f3f4",
    text: "#3c4043",
  };
};
const LectureCard: React.FC<LectureCardProps> = ({
  event,
  gridStartHour = 7,
  hourHeight = 80,
  left,
  right,
  width,
}) => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isCenteredPopover, setIsCenteredPopover] = useState(false);
  const [popoverPlacement, setPopoverPlacement] =
    useState<PopoverPlacement>("rightTop");
  const cardRef = useRef<HTMLDivElement>(null);

  const start = new Date(event.startTime);
  const end = new Date(event.endTime);

  const startHour = start.getHours();
  const startMin = start.getMinutes();

  // compute duration in minutes
  const durationMins = (end.getTime() - start.getTime()) / (1000 * 60);

  // Math for positioning
  const topOffset = (startHour - gridStartHour + startMin / 60) * hourHeight;
  const height = (durationMins / 60) * hourHeight;

  const isSingleInSlot = event.width == 100;
  const handleCardClick = (eventData: any) => {
    navigate(
      `/admin/schedule-beta/lecture-details/${eventData.batchTimeTableId}`,
      {
        state: {
          lecture: eventData,
          selectedSchedule: eventData,
          event: eventData,
          // This flag is read by MyScheduleBeta to skip refetching when going back
          from: "/admin/schedule-beta",
        },
      }
    );
  };
  const eventName = event.eventName ? event.eventName : event.eventType;
  const status = inBetweenTime(event?.startTime, event?.endTime)
    ? "Ongoing"
    : event.syllabusStatus;

  const calculatePlacement = (
    element?: HTMLElement | null
  ): PopoverPlacement => {
    const el = element || cardRef.current;
    if (!el) return "rightTop";

    const rect = el.getBoundingClientRect();

    const popoverWidth = 400;
    const popoverHeight = 380;
    const gap = 12;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const spaceOnRight = viewportWidth - rect.right;
    const spaceOnLeft = rect.left;

    const hasSpaceOnRight = spaceOnRight >= popoverWidth + gap;
    const hasSpaceOnLeft = spaceOnLeft >= popoverWidth + gap;

    // No space on either left or right.
    // This is your single/full-width lecture case.
    if (!hasSpaceOnRight && !hasSpaceOnLeft) {
      setIsCenteredPopover(true);

      // Just use a valid AntD placement.
      return "top";
    }

    setIsCenteredPopover(false);

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    const isRight = hasSpaceOnRight || spaceOnRight >= spaceOnLeft;

    const isTop = spaceBelow >= popoverHeight + gap || spaceBelow >= spaceAbove;

    if (isRight && isTop) return "rightTop";
    if (isRight && !isTop) return "rightBottom";
    if (!isRight && isTop) return "leftTop";

    return "leftBottom";
  };
  //  Decide whether the popover should open on the right or left, top or bottom
  const handlePopoverOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setPopoverPlacement(calculatePlacement(e.currentTarget));
    setPopoverOpen(true);
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setPopoverPlacement(calculatePlacement());
      setPopoverOpen(true);
    } else {
      setPopoverOpen(false);
    }
  };

  const onMbClose = () => {
    setPopoverOpen(false);
  };

  const cardDiv = (
    <div
      ref={cardRef}
      onClick={handlePopoverOpen}
      className={`lecture-card ${getColorCodeByLectureStatus(status)}`}
      style={{
        position: "absolute",
        top: `${topOffset}px`,
        height: `${height}px`,
        left:
          left !== undefined
            ? typeof left === "number"
              ? `${left}px`
              : left
            : "8px",
        right:
          width !== undefined
            ? undefined
            : right !== undefined
            ? typeof right === "number"
              ? `${right}px`
              : right
            : "16px",
        borderLeft: "4px solid",
        borderRadius: "8px",
        cursor: "pointer",
        boxSizing: "border-box",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        alignItems: "flex-start",
        textTransform: "none",
        width:
          width !== undefined
            ? typeof width === "number"
              ? `${width}px`
              : width
            : isSingleInSlot
            ? "calc(100% - 4px)"
            : undefined,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          textTransform: "none",
        }}
        className={getColorCodeByLectureStatus(status)}
      >
        {isSingleInSlot ? (
          <div
            style={{
              fontWeight: 800,
              fontSize: "12px",
            }}
          >
            {getTruncateLectureWithWidth(eventName || "", 33)}-
            {getBatchName(event?.batchName)}
          </div>
        ) : (
          <div
            style={{
              fontWeight: 800,
              fontSize: "12px",
            }}
          >
            {getTruncateLectureWithWidth(eventName || "", 33)}
          </div>
        )}
      </div>

      <div className="pl-1 d-flex flex-column justify-content-start align-items-start gap-2">
        {!isSingleInSlot && (
          <div className="ev-meta">
            <div>{getBatchName(event?.batchName)}</div>
          </div>
        )}
        {isSingleInSlot && (
          <>
            {event.subject && (
              <div
                style={{
                  fontSize: "11px",
                  color: "#666",
                }}
              >
                {event.subject} - {event.batchTimeTableId}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
  if (isMobile) {
    return (
      <>
        {cardDiv}
        <DraggableModal
          isPaneOpen={popoverOpen}
          togglePane={onMbClose}
          onClose={onMbClose}
          initHeight={340}
        >
          <LectureDetailsPopoverContent
            event={event}
            onClose={() => setPopoverOpen(false)}
            onOpenDetails={handleCardClick}
          />
        </DraggableModal>
      </>
    );
  }
  console.log("popoverPlacementpopoverPlacement", popoverPlacement);
  return (
    <Popover
      content={
        <LectureDetailsPopoverContent
          event={event}
          onClose={() => setPopoverOpen(false)}
          onOpenDetails={handleCardClick}
        />
      }
      trigger="click"
      open={popoverOpen}
      onOpenChange={handleOpenChange}
      placement={popoverPlacement}
      arrow={false}
      autoAdjustOverflow={!isCenteredPopover}
      getPopupContainer={() => document.body}
      overlayClassName={
        isCenteredPopover
          ? "custom-lecture-popover centered-lecture-popover"
          : "custom-lecture-popover"
      }
      overlayInnerStyle={{
        padding: 0,
        borderRadius: "16px",
        width: "400px",
        maxWidth: "calc(100vw - 20px)",
        boxSizing: "border-box",
        overflow: "visible",
      }}
    >
      {cardDiv}
    </Popover>
  );
};

export default LectureCard;
